/**
 * Личный кабинет ведущего на маршруте `/me` (Фаза 3, И4).
 *
 * Защищён auth-гейтом (как `/edit`): без сессии — `redirect('/login')`. Профиль
 * берётся из [`useAuth`](../useAuth.tsx) (bootstrap `/me`), список квизов — из
 * [`fetchMyGames`](../api.ts) (`GET /api/me/games`, только свои). Для каждого
 * квиза:
 * - «Провести» → старт сессии (POST /api/sessions) ЗДЕСЬ; при успехе сессия
 *   сохраняется через saveHost (resume) и [`onLaunchGame`](#) переключает на host-флоу
 *   (HostScreen подхватывает готовую сессию через loadHost, без повторного старта);
 *   при 409 limit_parallel_rooms — inline-сообщение у строки, без ухода с кабинета.
 * - «Редактировать» → [`onEditGame`](#) (открыть в редакторе с PUT-привязкой);
 * - «Удалить» — с `window.confirm` → [`deleteGame`](../api.ts), обновление списка.
 * Обрабатываются 403/404 удаления (дружелюбные сообщения) и пустое состояние.
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { saveHost, saveStartOptions } from '../activeSession';
import {
  ApiError,
  deleteGame,
  fetchMyActiveSessions,
  fetchMyGames,
  fetchMySessionHistory,
  fetchMyStats,
  getEffectiveLimits,
  LIMIT_PARALLEL_ROOMS,
  startSession,
  stopMySession,
  type EffectiveLimits,
  type MyActiveSession,
  type MyGame,
  type MySessionHistoryItem,
  type MyStats,
} from '../api';
import { DashboardOpenPanel } from './DashboardOpenPanel';
import { historyDuration } from '../util';
import { fetchOpenStatus } from '../api';
import { Link } from '../components/ui';
import { navigate, redirect } from '../router';
import { useAuth } from '../useAuth';
import { errorMessage } from '../util';

export interface DashboardProps {
  /** «Провести» квиз: запуск host-флоу по выбранному квизу (как из HostSelect). */
  onLaunchGame: (gameId: number) => void;
  /** «Редактировать»: открыть квиз в редакторе (/edit) с привязкой для PUT. */
  onEditGame: (gameId: number) => void;
  /** «Открыть» активную сессию: resume уже идущей сессии (saveHost вызывается внутри). */
  onResumeSession: (pin: string, sessionId: number) => void;
}

/** Дружелюбное сообщение об ошибке удаления по статусу (403/404/иное). */
function deleteErrorMessage(t: (key: string) => string, e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return t('errors.deleteForbidden');
    if (e.status === 404) return t('errors.gameNotFound');
  }
  return errorMessage(e);
}

/**
 * Дружелюбное сообщение об ошибке старта сессии (POST /api/sessions). Превышение
 * лимита параллельных комнат хоста (409 limit_parallel_rooms) переводится в
 * локализованное сообщение-подсказку; прочие ошибки — как есть (сырой код/текст).
 */
function launchErrorMessage(t: (key: string) => string, e: unknown): string {
  if (e instanceof ApiError && e.status === 409 && e.message === LIMIT_PARALLEL_ROOMS) {
    return t('errors.parallelRooms');
  }
  return errorMessage(e);
}

/**
 * Превью истории в кабинете (Фаза 12, B4): последние N сессий + ссылка «Вся
 * история (total)» на отдельный маршрут /me/history. Полная история с пагинацией
 * и inline-итогами живёт там — кабинет не тяжелеет от сотен строк (DOM ограничен).
 */
const HISTORY_PREVIEW = 10;

export function Dashboard({
  onLaunchGame,
  onEditGame,
  onResumeSession,
}: DashboardProps): JSX.Element {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [games, setGames] = useState<MyGame[]>([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [perGameError, setPerGameError] = useState<Record<number, string>>({});
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Какой квиз сейчас стартует в сессию (POST /api/sessions): блокирует кнопки строки.
  const [launchingId, setLaunchingId] = useState<number | null>(null);

  // ── Модал «Провести» (Фаза 13, C2): настройки старта перед запуском ────────
  // Кнопка «Провести» на карточке квиза сначала открывает модал с чекбоксами
  // (scorePause — опция И4; lockAfterStart/strictNick — Фаза 13). Решение
  // владельца 2026-11-02 (расширение Фазы 16): scorePause и strictNick теперь
  // ВКЛЮЧЕНЫ по умолчанию; галка scorePause инвертирована в «Скрывать счёт
  // после каждого вопроса» (state хранит wire-семантику: true = показывать).
  // Подтверждение сохраняет выбор (startOptions) и стартует сессию; лобби
  // HostSession предзаполняется выбором — финальная точка истины до `start`.
  const [launchModal, setLaunchModal] = useState<MyGame | null>(null);
  const [modalScorePause, setModalScorePause] = useState(true);
  const [modalLockAfterStart, setModalLockAfterStart] = useState(false);
  const [modalStrictNick, setModalStrictNick] = useState(true);
  const [modalAutoNext, setModalAutoNext] = useState(false);

  // ── Активные сессии ведущего (host-scoped /api/me/sessions) ─────────────────
  // Список идущих сейчас сессий этого хоста с кол-вом подключённых. Обновляется
  // при mount, по кнопке «Обновить» и после stop. Название игры обогащается из
  // «Мои квизы» по gameId (R2 плана): session.gameId → games[].title; если не
  // найдено (library-сессия) — fallback на PIN.
  const [sessions, setSessions] = useState<MyActiveSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [stoppingId, setStoppingId] = useState<number | null>(null);

  // ── История завершённых live-сессий хоста (Фаза 10, И6, /api/me; B4) ───────
  // Компактное превью: последние HISTORY_PREVIEW строк + ссылка «Вся история
  // (total)» на /me/history (полная история с пагинацией и итогами — там).
  // Сводка (stats) — отдельный non-blocking запрос: при его ошибке список живёт
  // без пилюль (как квота).
  const [history, setHistory] = useState<MySessionHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [stats, setStats] = useState<MyStats | null>(null);

  // ── Квота квизов (user-facing счётчик N/limit, Фаза 7, И6) ────────────────
  // Лимит quizzes_per_host грузится один раз из public /api/limits (без прав
  // админа). Показывается счётчик «Квизов: N / limit»; при достижении — hint.
  const [limits, setLimits] = useState<EffectiveLimits | null>(null);

  // ── Открытый доступ (Фаза 10, И12): панель публикации по квизу ────────────
  // Кнопка «Открытый доступ» в строке квиза → inline-панель (accordion, §C6.4).
  // Статусы публикаций подтягиваются лениво для счётчика квоты «N / limit»
  // (квизов у владельца мало — по одному дешёвому GET на квиз, тихие ошибки).
  const [openPanelId, setOpenPanelId] = useState<number | null>(null);
  const [openPublished, setOpenPublished] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (games.length === 0) return;
    let cancelled = false;
    void Promise.allSettled(
      games.map(async (g) => [g.id, (await fetchOpenStatus(g.id)).published] as const),
    ).then((results) => {
      if (cancelled) return;
      const next: Record<number, boolean> = {};
      for (const r of results) {
        if (r.status === 'fulfilled') next[r.value[0]] = r.value[1];
      }
      setOpenPublished(next);
    });
    return () => {
      cancelled = true;
    };
  }, [games]);

  /** Число опубликованных открытых квизов (для бейджа квоты панели). */
  const publishedOpenCount = Object.values(openPublished).filter(Boolean).length;

  useEffect(() => {
    let cancelled = false;
    getEffectiveLimits()
      .then((l) => {
        if (!cancelled) setLimits(l);
      })
      .catch(() => {
        // Non-blocking: счётчик опционален; если /api/limits недоступен —
        // UI просто не показывает счётчик (квизы и удаление работают).
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Гейт: без auth → /login. Дублирует эффект App для устойчивости (и чтобы
  // экран был самодостаточным/тестируемым вне диспетчера).
  useEffect(() => {
    if (!loading && !user) redirect('/login');
  }, [loading, user]);

  const refresh = useCallback(async () => {
    if (!user) return;
    setGamesLoading(true);
    setError(null);
    try {
      setGames(await fetchMyGames());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setGamesLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Загрузка активных сессий хоста (GET /api/me/sessions/active). Обновляется
  // независимо от «Мои квизы»: при mount, по кнопке «Обновить» секции и после stop.
  const refreshSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      setSessions(await fetchMyActiveSessions());
    } catch (e) {
      setSessionsError(errorMessage(e));
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshSessions();
  }, [refreshSessions]);

  // Сводка-статистика (Ц3): отдельный ленивый non-blocking запрос — при ошибке
  // секция списка живёт без пилюль (паттерн квоты getEffectiveLimits).
  useEffect(() => {
    let cancelled = false;
    fetchMyStats()
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch(() => {
        // Non-blocking: сводка опциональна.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Превью истории (GET /api/me/sessions/history, limit=HISTORY_PREVIEW).
  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const resp = await fetchMySessionHistory({ limit: HISTORY_PREVIEW, offset: 0 });
      setHistory(resp.items);
      setHistoryTotal(resp.total);
    } catch (e) {
      setHistoryError(errorMessage(e));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);

  async function handleDelete(game: MyGame): Promise<void> {
    if (!window.confirm(t('dashboard.confirmDelete', { title: game.title }))) return;
    setDeletingId(game.id);
    // Сбрасываем прежнюю ошибку этого квиза перед новой попыткой.
    setPerGameError((prev) => {
      const next = { ...prev };
      delete next[game.id];
      return next;
    });
    try {
      await deleteGame(game.id);
      setGames((prev) => prev.filter((g) => g.id !== game.id));
    } catch (e) {
      setPerGameError((prev) => ({ ...prev, [game.id]: deleteErrorMessage(t, e) }));
    } finally {
      setDeletingId(null);
    }
  }

  // Старт сессии ведущего (POST /api/sessions) из кабинета. Сессия создаётся ЗДЕСЬ,
  // до перехода в host-флоу: при ошибке лимита параллельных комнат (409
  // limit_parallel_rooms) показываем inline-сообщение у строки квиза и ОСТАЁМСЯ на
  // /me (без редиректа на витрину HostSelect). При успехе сохраняем сессию через
  // saveHost (resume): HostScreen при mount подхватит её через loadHost и сразу
  // перейдёт к HostSession, НЕ вызывая startSession повторно (иначе — дубль комнаты,
  // который при limits.parallel_rooms=1 сам бы вызвал ложный 409).
  /** Открыть модал «Провести»: дефолты по решению 2026-11-02 (расширение
   *  Фазы 16) — показ счёта после каждого вопроса (scorePause=true) и строгие
   *  ники (strictNick=true) включены; lockAfterStart/autoNext выключены. */
  function openLaunchModal(game: MyGame): void {
    setModalScorePause(true);
    setModalLockAfterStart(false);
    setModalStrictNick(true);
    setModalAutoNext(false);
    setLaunchModal(game);
  }

  /**
   * Подтверждение модала: сохранить настройки (их прочтёт лобби ведущего) и
   * стартовать сессию. Значения уходят в start.payload лобби → сервер.
   */
  async function confirmLaunch(): Promise<void> {
    const game = launchModal;
    if (!game) return;
    saveStartOptions({
      scorePause: modalScorePause,
      lockAfterStart: modalLockAfterStart,
      strictNick: modalStrictNick,
      autoNext: modalAutoNext,
    });
    setLaunchModal(null);
    await handleLaunch(game);
  }

  async function handleLaunch(game: MyGame): Promise<void> {
    setLaunchingId(game.id);
    setPerGameError((prev) => {
      const next = { ...prev };
      delete next[game.id];
      return next;
    });
    try {
      const session = await startSession(game.id);
      saveHost(session.pin, session.sessionId);
      onLaunchGame(game.id);
    } catch (e) {
      setPerGameError((prev) => ({ ...prev, [game.id]: launchErrorMessage(t, e) }));
    } finally {
      setLaunchingId(null);
    }
  }

  // «Открыть» — resume уже идущей сессии БЕЗ повторного startSession (иначе дубль
  // комнаты и ложный 409 limit_parallel_rooms). saveHost перезаписывает
  // sessionStorage активной host-сессии; onResumeSession переключает на host-флоу,
  // где HostScreen при mount подхватит {pin, sessionId} через loadHost.
  function handleResumeSession(s: MyActiveSession): void {
    saveHost(s.pin, s.sessionId);
    onResumeSession(s.pin, s.sessionId);
  }

  // «Завершить» — остановка своей сессии (POST /api/me/sessions/{id}/stop). После
  // подтверждения вызываем stop, локально убираем строку (оптимистично) и
  // рефетчим список для консистентности (могли подключиться/отключиться игроки).
  // 404 трактуем как «уже завершена/не активна» — дружелюбное сообщение.
  async function handleStopSession(s: MyActiveSession): Promise<void> {
    if (!window.confirm(t('dashboard.sessions.confirmStop', { pin: s.pin }))) return;
    setStoppingId(s.sessionId);
    setSessionsError(null);
    try {
      await stopMySession(s.sessionId);
      setSessions((prev) => prev.filter((x) => x.sessionId !== s.sessionId));
      void refreshSessions();
    } catch (e) {
      setSessionsError(
        e instanceof ApiError && e.status === 404
          ? t('dashboard.sessions.notActive')
          : errorMessage(e),
      );
    } finally {
      setStoppingId(null);
    }
  }

  // Пока идёт bootstrap либо пользователя нет — показываем заглушку (редирект на
  // /login стартует в эффекте выше после завершения bootstrap).
  if (loading || !user) {
    return (
      <main className="card card--page">
        <p className="muted" data-testid="me-gate">
          {loading ? t('common.loading') : t('dashboard.gateGuest')}
        </p>
      </main>
    );
  }

  // Карта обогащения названий игр (R2 плана): gameId → title из «Мои квизы».
  // Library-сессии здесь не найдутся → в карточке показывается fallback по PIN.
  const gameTitleById = new Map<number, string>(games.map((g) => [g.id, g.title]));

  return (
    <main className="card card--wide" aria-labelledby="me-title" data-testid="dashboard">
      <header className="screen-head">
        <h2 id="me-title" className="block-title">
          {t('dashboard.title')}
        </h2>
        <div className="screen-head-meta">
          <Link to="/" className="btn btn--ghost">
            {t('nav.toMain')}
          </Link>
        </div>
      </header>

      <section className="block">
        <div className="dashboard-profile" data-testid="dashboard-profile">
          <div className="dashboard-profile__main">
            <span className="dashboard-profile__name" data-testid="me-username">
              {user.username}
            </span>
            {user.fullName ? (
              <span className="dashboard-profile__full" data-testid="me-fullname">
                {user.fullName}
              </span>
            ) : null}
            <span className="dashboard-profile__email" data-testid="me-email">
              {user.email}
            </span>
          </div>
          <span
            className={`pill pill--verified${user.verified ? '' : ' pill--unverified'}`}
            data-testid="me-verified"
          >
            {user.verified ? t('dashboard.verified') : t('dashboard.unverified')}
          </span>
        </div>
      </section>

      {/* Активные сессии ведущего: идущие сейчас игры этого хоста (host-scoped
          /api/me/sessions). Выше «Мои квизы», т.к. активные первичны «в процессе».
          Кнопка создания квиза живёт в «Мои квизы» (B2): относилась не к сессиям. */}
      <section className="block">
        <div className="block-head">
          <h3 className="block-subtitle">{t('dashboard.sessions.title')}</h3>
          <div className="block-head-meta">
            <button
              type="button"
              className="link-btn"
              onClick={() => void refreshSessions()}
              disabled={sessionsLoading || stoppingId !== null}
            >
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {sessionsLoading ? (
          <p className="muted" data-testid="me-sessions-loading">
            {t('dashboard.sessions.loading')}
          </p>
        ) : sessionsError ? (
          <p className="error" role="alert" data-testid="me-sessions-error">
            {sessionsError}
          </p>
        ) : sessions.length === 0 ? (
          <p className="muted" data-testid="me-sessions-empty">
            {t('dashboard.sessions.empty')}
          </p>
        ) : (
          <ul className="game-list" data-testid="me-sessions-list">
            {sessions.map((s) => {
              const gameTitle = gameTitleById.get(s.gameId);
              return (
                <li
                  key={s.sessionId}
                  className="game-row dashboard-game"
                  data-testid="me-session-row"
                >
                  <span className="dashboard-game__title">
                    {gameTitle ?? t('dashboard.sessions.pinFallback', { pin: s.pin })}
                  </span>
                  <span className="muted" data-testid="me-session-pin">
                    PIN: {s.pin}
                  </span>
                  <span className="muted" data-testid="me-session-players">
                    {t('dashboard.sessions.players', { n: s.playerCount })}
                  </span>
                  <span className="muted" data-testid="me-session-phase">
                    {t(`dashboard.sessions.phase.${s.phase}`, { defaultValue: s.phase })}
                  </span>
                  <div className="dashboard-game__actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => handleResumeSession(s)}
                      disabled={stoppingId !== null}
                      data-testid="me-session-open"
                    >
                      {t('dashboard.sessions.open')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void handleStopSession(s)}
                      disabled={stoppingId !== null}
                      data-testid="me-session-stop"
                    >
                      {stoppingId === s.sessionId
                        ? t('dashboard.sessions.stopping')
                        : t('dashboard.sessions.stop')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="block">
        <div className="block-head">
          <h3 className="block-subtitle">{t('dashboard.myGames')}</h3>
          <div className="block-head-meta">
            {limits ? (
              <span className="muted" data-testid="dashboard-quota">
                {t('dashboard.quota.quizzesCount', {
                  n: games.length,
                  limit: limits.quizzesPerHost,
                })}
              </span>
            ) : null}
            <button
              type="button"
              className="link-btn"
              onClick={() => void refresh()}
              disabled={gamesLoading}
            >
              {t('common.refresh')}
            </button>
            {/* «Создать квиз» (B2): перенесена из шапки «Активные сессии» — кнопка
                относится к квизам, а не к сессиям. Ключ dashboard.createGame. */}
            <button
              type="button"
              className="btn"
              onClick={() => navigate('/edit')}
              data-testid="me-games-create"
            >
              {t('dashboard.createGame')}
            </button>
          </div>
        </div>

        {limits && limits.quizzesPerHost > 0 && games.length >= limits.quizzesPerHost ? (
          <p className="error" role="alert" data-testid="dashboard-quota-warn">
            {t('dashboard.quota.quotaReached')}
          </p>
        ) : null}

        {gamesLoading ? (
          <p className="muted" data-testid="me-games-loading">
            {t('dashboard.gamesLoading')}
          </p>
        ) : error ? (
          <p className="error" role="alert" data-testid="me-games-error">
            {error}
          </p>
        ) : games.length === 0 ? (
          <div className="dashboard-empty" data-testid="me-games-empty">
            <p className="muted">{t('dashboard.empty')}</p>
            <Link to="/edit" className="cta dashboard-empty__cta">
              {t('dashboard.createGame')}
            </Link>
          </div>
        ) : (
          <ul className="game-list" data-testid="me-games-list">
            {games.map((g) => (
              <li key={g.id} className="game-row dashboard-game" data-testid="me-game-row">
                <span className="dashboard-game__title">{g.title}</span>
                <div className="dashboard-game__actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => openLaunchModal(g)}
                    disabled={deletingId !== null || launchingId !== null}
                    data-testid="me-game-launch"
                  >
                    {launchingId === g.id ? t('dashboard.launching') : t('host.launch')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => onEditGame(g.id)}
                    disabled={deletingId !== null}
                    data-testid="me-game-edit"
                  >
                    {t('dashboard.edit')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => void handleDelete(g)}
                    disabled={deletingId !== null}
                    data-testid="me-game-delete"
                  >
                    {deletingId === g.id ? t('dashboard.deleting') : t('common.delete')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setOpenPanelId((prev) => (prev === g.id ? null : g.id))}
                    data-testid="me-game-open"
                  >
                    {t('dashboard.open.toggle')}
                  </button>
                </div>
                {perGameError[g.id] ? (
                  <p className="error dashboard-game__error" role="alert">
                    {perGameError[g.id]}
                  </p>
                ) : null}
                {openPanelId === g.id ? (
                  <DashboardOpenPanel
                    gameId={g.id}
                    publishLimit={limits ? limits.openQuizzesPerUser : null}
                    publishedCount={publishedOpenCount}
                    onPublishedChange={(published) =>
                      setOpenPublished((prev) => ({ ...prev, [g.id]: published }))
                    }
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* История завершённых live-сессий (Фаза 10, И6 → Фаза 12, B4): самая
          «холодная» зона кабинета — компактное превью последних HISTORY_PREVIEW
          строк (без inline-итогов) + ссылка «Вся история (total)» на отдельный
          маршрут /me/history с оконной пагинацией и итогами (FinishScreen-подиум
          + ResultsBoard переехали туда). Сводка-пилюли из /api/me/stats остаются. */}
      <section className="block">
        <div className="block-head">
          <h3 className="block-subtitle">{t('dashboard.history.title')}</h3>
          <div className="block-head-meta">
            {historyTotal > 0 ? (
              <Link to="/me/history" className="link-btn" data-testid="me-history-all">
                {t('dashboard.history.all', { n: historyTotal })}
              </Link>
            ) : null}
            <button
              type="button"
              className="link-btn"
              onClick={() => void refreshHistory()}
              disabled={historyLoading}
            >
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {stats ? (
          <p className="history-stats" data-testid="me-history-stats">
            <span className="pill">
              {t('dashboard.history.stats.games', { n: stats.sessionsFinished })}
            </span>
            <span className="pill">
              {t('dashboard.history.stats.players', { n: stats.totalPlayers })}
            </span>
          </p>
        ) : null}

        {historyLoading && history.length === 0 ? (
          <p className="muted" data-testid="me-history-loading">
            {t('dashboard.history.loading')}
          </p>
        ) : historyError ? (
          <p className="error" role="alert" data-testid="me-history-error">
            {historyError}
          </p>
        ) : history.length === 0 ? (
          <p className="muted" data-testid="me-history-empty">
            {t('dashboard.history.empty')}
          </p>
        ) : (
          <ul className="game-list" data-testid="me-history-list">
            {history.map((s) => (
              <li key={s.id} className="game-row dashboard-game" data-testid="me-history-row">
                <span className="dashboard-game__title" data-testid="me-history-title">
                  {s.gameTitle}
                </span>
                <span className="muted" data-testid="me-history-date">
                  {new Date(s.finishedAt).toLocaleString()}
                </span>
                <span className="muted" data-testid="me-history-players">
                  {t('dashboard.history.players', { n: s.playerCount })}
                </span>
                <span className="muted" data-testid="me-history-duration">
                  {historyDuration(t, s.durationSec)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Модал «Провести» (Фаза 13, C2): настройки старта сессии. Подтверждение
          сохраняет их для лобби ведущего (startOptions) и стартует сессию.
          Порталится в document.body (createPortal): .modal-overlay с
          position:fixed внутри .card (backdrop-filter: blur) в Chromium
          позиционировался бы относительно КАРТОЧКИ (containing block), а не
          вьюпорта — на длинном кабинете модалка оказывалась вне экрана при
          прокрутке (замечание игротеста 0.14.1). Тот же паттерн, что у модалей
          редактора (EditorJsonPanel/EditorJsonInsert, Фаза 12 D3/D4). */}
      {launchModal
        ? createPortal(
            <div className="modal-overlay">
              <div
                className="modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="launch-modal-title"
                data-testid="me-launch-modal"
              >
                <div className="modal__head">
                  <h3 id="launch-modal-title" className="modal__title">
                    {t('dashboard.launch.title', { title: launchModal.title })}
                  </h3>
                </div>
                <div className="modal__body">
                  {/* Решение 2026-11-02: галка инвертирована — «Скрывать счёт
                      после каждого вопроса». Снята (дефолт) → scorePause=true
                      (счёт показывается); отмечена → scorePause=false. */}
                  <label className="lobby-option">
                    <input
                      type="checkbox"
                      checked={!modalScorePause}
                      onChange={(e) => setModalScorePause(!e.target.checked)}
                      data-testid="me-start-score-pause"
                    />
                    {t('lobby.scorePause')}
                  </label>
                  <label className="lobby-option">
                    <input
                      type="checkbox"
                      checked={modalLockAfterStart}
                      onChange={(e) => setModalLockAfterStart(e.target.checked)}
                      data-testid="me-start-lock-after-start"
                    />
                    {t('lobby.lockAfterStart')}
                  </label>
                  <label className="lobby-option">
                    <input
                      type="checkbox"
                      checked={modalStrictNick}
                      onChange={(e) => setModalStrictNick(e.target.checked)}
                      data-testid="me-start-strict-nick"
                    />
                    {t('lobby.strictNick')}
                  </label>
                  {/* Фаза 16 (A): авто-переход после reveal — серверный таймер
                      сам листает вопросы; ручной «Следующий» остаётся. */}
                  <label className="lobby-option">
                    <input
                      type="checkbox"
                      checked={modalAutoNext}
                      onChange={(e) => setModalAutoNext(e.target.checked)}
                      data-testid="me-start-auto-next"
                    />
                    {t('lobby.autoNext')}
                  </label>
                  <div className="btn-row">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => setLaunchModal(null)}
                      data-testid="me-launch-cancel"
                    >
                      {t('dashboard.launch.cancel')}
                    </button>
                    <button
                      type="button"
                      className="cta"
                      onClick={() => void confirmLaunch()}
                      disabled={launchingId !== null}
                      data-testid="me-launch-confirm"
                    >
                      {launchingId === launchModal.id ? t('dashboard.launching') : t('host.launch')}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </main>
  );
}
